import React, { useState, useEffect, useRef, useDeferredValue } from 'react';
import aviso, { textoDoErro } from '../lib/aviso';
import {
    LuCopy, LuDownload, LuRefreshCw, LuImage, LuExternalLink, LuPlus, LuX, LuInfo,
} from 'react-icons/lu';
import { QRCodeSVG } from 'qrcode.react';
import PageHeader from '../components/PageHeader';
import LandingPagePreview from '../components/LandingPagePreview';
import GerenciarEmpresaService, { getMidiaUrl } from '../services/GerenciarEmpresaService';
import { useColunaRedimensionavel } from '../hooks/useColunaRedimensionavel';
import './PaginaDeCapturaPage.css';

const OPCOES_PADRAO = [
    'Hotéis / Pousada', 'Instagram', 'Já conhecia a loja',
    'CAT', 'Pontos turísticos da cidade', 'Outros',
];

/* A coluna da prévia era fixa em 340px, e sobravam 308 úteis para desenhar uma
   página que no celular tem 390 — a prévia mostrava a tela a 79% do tamanho.
   O editor não usa a largura que a prévia ganha: `.pc-sec` trava em 560px de
   conteúdo, então a partir daí o espaço não tem dono.

   O encaixe é MEDIDO, não fixo: entre a borda da coluna e o palco da prévia há
   padding em `rem`, e a raiz do app é `clamp(13px, 1vw, 16px)` — a mesma régua
   fluida que obrigou a página pública a ser toda em px. Um encaixe fixo em 419
   acerta em 1280 e erra por 3px em 1440. Medir a diferença entre a coluna e o
   palco resolve em qualquer largura. */
const LARGURA_DA_PAGINA = 390;

const larguraParaTamanhoReal = (): number | null => {
    const coluna = document.querySelector('.pc-lado');
    const palco = document.querySelector('.lpp-palco');
    if (!coluna || !palco) return null;
    const moldura = coluna.getBoundingClientRect().width - palco.getBoundingClientRect().width;
    return LARGURA_DA_PAGINA + moldura;
};

const PREVIA = {
    chave: 'pc:largura-previa',
    padrao: 340,
    minimo: 340,
    maximo: 581,
    minimoDoOutroLado: 603,
    reserva: 9,
    encaixe: larguraParaTamanhoReal,
};

const PaginaDeCapturaPage = () => {
    const [carregando, setCarregando] = useState(true);
    const [salvando, setSalvando] = useState(false);
    const previa = useColunaRedimensionavel(PREVIA);

    const [token, setToken] = useState('');
    const [logoPath, setLogoPath] = useState<string | null>(null);
    /* A cor primária própria da página. Nula = herda a cor da marca, que mora
       em Gerenciar empresa (05/10/2026) — a mesma regra do logo, que vazio
       herda a logo principal. Só sem as duas entra o verde de sempre. */
    const [corPropria, setCorPropria] = useState<string | null>(null);
    const [corMarca, setCorMarca] = useState<string | null>(null);
    const corPrimaria = corPropria ?? corMarca ?? '#0a5c5a';
    const [corFundo, setCorFundo] = useState('#0d0f12');
    const [corTexto, setCorTexto] = useState('#ffffff');
    const [cupomCodigo, setCupomCodigo] = useState('');
    const [cupomTexto, setCupomTexto] = useState('');
    const [cupomAtivo, setCupomAtivo] = useState(false);
    const [opcoes, setOpcoes] = useState<string[]>([]);
    const [novaOpcao, setNovaOpcao] = useState('');

    /* A chamada e o agradecimento eram texto fixo no código do front; os três
       interruptores decidem o tamanho do formulário público. Nascem ligados:
       quem já usa a página recebe as mesmas perguntas de antes. */
    const [chamada, setChamada] = useState('');
    const [apoio, setApoio] = useState('');
    const [agradecimento, setAgradecimento] = useState('');
    const [pedeOrigem, setPedeOrigem] = useState(true);
    const [pedeEmail, setPedeEmail] = useState(true);
    const [pedeCidade, setPedeCidade] = useState(true);

    /* O lojista mexe em interruptor; o que ele quer saber é quantas telas o
       cliente vai ver. A conta é a mesma de PublicLandingPage.montarPassos. */
    const quantosPassos = 2
        + (pedeOrigem && opcoes.length > 0 ? 1 : 0)
        + (pedeEmail || pedeCidade ? 1 : 0);
    const [nomeEmpresa, setNomeEmpresa] = useState('Sua Empresa');

    /* A prévia redesenha a cada tecla do seletor de cor. `useDeferredValue`
       mantém os campos responsivos enquanto ela alcança. */
    const corPrimariaAdiada = useDeferredValue(corPrimaria);
    const corFundoAdiada = useDeferredValue(corFundo);
    const corTextoAdiada = useDeferredValue(corTexto);

    const refArquivo = useRef<HTMLInputElement>(null);
    const refQr = useRef<HTMLDivElement>(null);

    useEffect(() => {
        carregar();
        GerenciarEmpresaService.getDadosGerais()
            .then(d => { if (d.nomeEmpresa) setNomeEmpresa(d.nomeEmpresa); setCorMarca(d.corMarca ?? null); })
            .catch(() => { });
    }, []);

    const carregar = async () => {
        setCarregando(true);
        try {
            const d = await GerenciarEmpresaService.getLandingPageConfig();
            setToken(d.publicPageToken || '');
            setLogoPath(d.publicPageLogoPath || null);
            setCorPropria(d.publicPagePrimaryColor || null);
            setCorFundo(d.publicPageBackgroundColor || '#0d0f12');
            setCorTexto(d.publicPageTextColor || '#ffffff');
            setCupomCodigo(d.promoCode || '');
            setCupomTexto(d.promoText || '');
            setCupomAtivo(d.promoActive || false);
            setOpcoes(d.configuredSegments
                ? d.configuredSegments.split(',').map((s: string) => s.trim()).filter(Boolean)
                : OPCOES_PADRAO);
            setChamada(d.publicPageHeadline || '');
            setApoio(d.publicPageSubheadline || '');
            setAgradecimento(d.publicPageThanks || '');
            // API mais antiga que este front não manda os interruptores: ligados.
            setPedeOrigem(d.publicPageAskSegment ?? true);
            setPedeEmail(d.publicPageAskEmail ?? true);
            setPedeCidade(d.publicPageAskCity ?? true);
        } catch (e: any) {
            aviso.erro(textoDoErro(e, 'Não foi possível carregar a página de captura.'));
        } finally {
            setCarregando(false);
        }
    };

    const salvar = async () => {
        setSalvando(true);
        try {
            await GerenciarEmpresaService.updateLandingPageConfig({
                publicPagePrimaryColor: corPropria,
                publicPageBackgroundColor: corFundo,
                publicPageTextColor: corTexto,
                promoCode: cupomCodigo,
                promoText: cupomTexto,
                promoActive: cupomAtivo,
                configuredSegments: opcoes.join(', '),
                publicPageHeadline: chamada.trim() || null,
                publicPageSubheadline: apoio.trim() || null,
                publicPageThanks: agradecimento.trim() || null,
                publicPageAskSegment: pedeOrigem,
                publicPageAskEmail: pedeEmail,
                publicPageAskCity: pedeCidade,
            });
            aviso.ok('Página de captura salva.');
        } catch (e: any) {
            aviso.erro(textoDoErro(e, 'Não foi possível salvar a página de captura.'));
        } finally {
            setSalvando(false);
        }
    };

    /* ─── Opções do formulário ───────────────────────────────── */

    const adicionarOpcao = () => {
        const v = novaOpcao.trim();
        if (!v) return;
        if (opcoes.includes(v)) { aviso.atencao('Essa opção já está na lista.'); return; }
        setOpcoes([...opcoes, v]);
        setNovaOpcao('');
    };

    /* ─── Logo ───────────────────────────────────────────────── */

    const enviarLogo = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const arquivo = e.target.files?.[0];
        if (!arquivo) return;
        const id = aviso.carregando('Enviando logo…');
        try {
            const r = await GerenciarEmpresaService.updateLandingPageLogo(arquivo);
            setLogoPath(r.caminhoRelativo);
            aviso.ok('Logo atualizada.', { id });
        } catch (err: any) {
            aviso.erro(textoDoErro(err, 'Não foi possível enviar a logo.'), { id });
        } finally {
            if (refArquivo.current) refArquivo.current.value = '';
        }
    };

    /* ─── Link e QR ──────────────────────────────────────────── */

    const urlPublica = `${window.location.origin}/p/${token}`;

    const copiarLink = () => {
        navigator.clipboard.writeText(urlPublica);
        aviso.ok('Link copiado.');
    };

    const gerarNovoToken = async () => {
        if (!window.confirm('Gerar um novo link público? O QR Code e o link atuais deixam de funcionar.')) return;
        try {
            const d = await GerenciarEmpresaService.generateLandingPageToken();
            setToken(d.publicPageToken);
            aviso.ok('Novo link público gerado.');
        } catch (e: any) {
            aviso.erro(textoDoErro(e, 'Não foi possível gerar o novo link.'));
        }
    };

    /* O QR é SVG na tela; para baixar vira PNG de 1000px com margem branca,
       que é o que serve para impressão em balcão. */
    const baixarQr = () => {
        const svg = refQr.current?.querySelector('svg');
        if (!svg) return;
        const texto = new XMLSerializer().serializeToString(svg);
        const blob = new Blob([texto], { type: 'image/svg+xml;charset=utf-8' });
        const urlBlob = URL.createObjectURL(blob);

        const img = new Image();
        img.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width = 1000;
            canvas.height = 1000;
            const ctx = canvas.getContext('2d');
            if (ctx) {
                ctx.fillStyle = '#FFFFFF';
                ctx.fillRect(0, 0, 1000, 1000);
                ctx.drawImage(img, 50, 50, 900, 900);
                const link = document.createElement('a');
                link.href = canvas.toDataURL('image/png');
                link.download = `qrcode_captura_${token.substring(0, 6)}.png`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            }
            URL.revokeObjectURL(urlBlob);
        };
        img.src = urlBlob;
    };

    const campoCor = (id: string, rotulo: string, valor: string, setValor: (v: string) => void, apoio?: React.ReactNode) => (
        <div className="pc-cor">
            <label className="pc-lab" htmlFor={id}>{rotulo}</label>
            <div className="pc-cor-linha">
                <input id={id} type="color" className="pc-cor-poco"
                    value={valor} onChange={e => setValor(e.target.value)} />
                <input type="text" className="pc-inp pc-cor-hex"
                    value={valor} onChange={e => setValor(e.target.value)} />
            </div>
            {apoio}
        </div>
    );

    const apoioPrimaria = corPropria === null
        ? <p className="pc-hint">{corMarca ? 'Herdada da cor da marca.' : 'Sem cor da marca em Gerenciar empresa: usando a padrão.'}</p>
        : corMarca && corPropria.toLowerCase() !== corMarca.toLowerCase()
            ? <button type="button" className="pc-link-btn pc-cor-herdar" onClick={() => setCorPropria(null)}>Usar a cor da marca</button>
            : null;

    return (
        <div className="page-shell pc-screen">
            <PageHeader
                title="Página de captura"
                subtitle="A página pública onde o cliente se cadastra sozinho."
                actions={
                    <div className="pc-acts">
                        <a className="pc-btn" href={urlPublica} target="_blank" rel="noreferrer">
                            <LuExternalLink size={13} /> Ver página
                        </a>
                        <button type="button" className="pc-btn is-primary" onClick={salvar} disabled={salvando || carregando}>
                            {salvando ? 'Salvando…' : 'Salvar'}
                        </button>
                    </div>
                }
            />

            <div className="page-body pc-fill">
                {carregando ? (
                    <div className="pc-carregando">Carregando a página de captura…</div>
                ) : (
                    <div
                        className={`pc${previa.arrastando ? ' pc-arrastando' : ''}`}
                        ref={previa.containerRef}
                        style={{ ['--pc-lado-w' as string]: `${previa.largura}px` }}
                    >
                        {/* ─── Editor ─────────────────────────── */}
                        <div className="pc-editor">

                            <section className="pc-sec">
                                <h2 className="pc-sec-tit">Visual</h2>

                                <div className="pc-campo">
                                    <span className="pc-lab">Logo da página</span>
                                    <div className="pc-logo-linha">
                                        {logoPath
                                            ? <img className="pc-logo" src={getMidiaUrl(logoPath)} alt=""
                                                onError={e => { (e.currentTarget as HTMLImageElement).style.visibility = 'hidden'; }} />
                                            : <span className="pc-logo pc-logo-vazia"><LuImage size={16} /></span>}
                                        <div>
                                            <input ref={refArquivo} type="file" accept="image/*" hidden onChange={enviarLogo} />
                                            <button type="button" className="pc-btn" onClick={() => refArquivo.current?.click()}>
                                                <LuImage size={13} /> Escolher logo
                                            </button>
                                            <p className="pc-hint">Vazio herda a logo principal da empresa.</p>
                                        </div>
                                    </div>
                                </div>

                                <div className="pc-cores">
                                    {campoCor('pc-primaria', 'Cor primária', corPrimaria, setCorPropria, apoioPrimaria)}
                                    {campoCor('pc-fundo', 'Cor de fundo', corFundo, setCorFundo)}
                                    {campoCor('pc-texto', 'Cor do texto', corTexto, setCorTexto)}
                                </div>
                            </section>

                            <section className="pc-sec">
                                <h2 className="pc-sec-tit">A chamada da página</h2>
                                <p className="pc-sec-sub">
                                    A primeira frase que o cliente lê, antes de preencher qualquer coisa.
                                </p>

                                <div className="pc-campo">
                                    <label className="pc-lab" htmlFor="pc-chamada">Título</label>
                                    <input id="pc-chamada" type="text" className="pc-inp" maxLength={120}
                                        value={chamada}
                                        placeholder={cupomAtivo && cupomTexto ? `Ganhe ${cupomTexto}` : `Cadastre-se na ${nomeEmpresa}`}
                                        onChange={e => setChamada(e.target.value)} />
                                    <p className="pc-hint">
                                        {cupomAtivo
                                            ? 'Vazio usa o texto do cupom, logo abaixo.'
                                            : 'Vazio usa o nome da empresa.'}
                                    </p>
                                </div>

                                <div className="pc-campo">
                                    <label className="pc-lab" htmlFor="pc-apoio">Linha de apoio</label>
                                    <input id="pc-apoio" type="text" className="pc-inp" maxLength={160}
                                        value={apoio}
                                        placeholder="Leva 30 segundos. Só o nome e o WhatsApp são obrigatórios."
                                        onChange={e => setApoio(e.target.value)} />
                                </div>
                            </section>

                            <section className="pc-sec">
                                <h2 className="pc-sec-tit">O que perguntar</h2>
                                <p className="pc-sec-sub">
                                    Nome e WhatsApp são sempre pedidos. O resto é escolha sua — cada
                                    campo a mais é gente que desiste no meio.
                                </p>

                                <div className="pc-switches">
                                    <button type="button"
                                        className={`pc-switch ${pedeOrigem ? 'is-on' : ''}`}
                                        role="switch" aria-checked={pedeOrigem}
                                        onClick={() => setPedeOrigem(!pedeOrigem)}>
                                        <span className="pc-switch-track"><i /></span>
                                        <span>Como o cliente conheceu a empresa</span>
                                    </button>

                                    <button type="button"
                                        className={`pc-switch ${pedeEmail ? 'is-on' : ''}`}
                                        role="switch" aria-checked={pedeEmail}
                                        onClick={() => setPedeEmail(!pedeEmail)}>
                                        <span className="pc-switch-track"><i /></span>
                                        <span>E-mail</span>
                                    </button>

                                    <button type="button"
                                        className={`pc-switch ${pedeCidade ? 'is-on' : ''}`}
                                        role="switch" aria-checked={pedeCidade}
                                        onClick={() => setPedeCidade(!pedeCidade)}>
                                        <span className="pc-switch-track"><i /></span>
                                        <span>Cidade onde mora</span>
                                    </button>
                                </div>

                                <p className="pc-hint">
                                    {quantosPassos === 2
                                        ? 'A página fica com 2 telas: nome e WhatsApp.'
                                        : `A página fica com ${quantosPassos} telas, uma pergunta por vez.`}
                                </p>
                            </section>

                            {/* Com a pergunta desligada, esta lista não alimenta nada:
                                bloco que não afeta a página é pior que bloco ausente.
                                As opções ficam salvas e voltam quando religar. */}
                            <section className="pc-sec" hidden={!pedeOrigem}>
                                <h2 className="pc-sec-tit">Como o cliente conheceu a empresa</h2>
                                <p className="pc-sec-sub">
                                    As opções que aparecem no formulário da página pública.
                                </p>

                                <div className="pc-chips">
                                    {opcoes.map((o, i) => (
                                        <span key={o} className="pc-chip">
                                            {o}
                                            <button type="button" className="pc-chip-x" aria-label={`Remover ${o}`}
                                                onClick={() => setOpcoes(opcoes.filter((_, idx) => idx !== i))}>
                                                <LuX size={11} />
                                            </button>
                                        </span>
                                    ))}
                                    {opcoes.length === 0 && (
                                        <p className="pc-alerta">
                                            <LuInfo size={13} />
                                            Sem opções, o campo "como conheceu" some da página pública.
                                        </p>
                                    )}
                                </div>

                                <div className="pc-add">
                                    <input type="text" className="pc-inp" placeholder="Ex.: Facebook"
                                        value={novaOpcao}
                                        onChange={e => setNovaOpcao(e.target.value)}
                                        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); adicionarOpcao(); } }} />
                                    <button type="button" className="pc-btn" onClick={adicionarOpcao}>
                                        <LuPlus size={13} /> Adicionar
                                    </button>
                                </div>
                            </section>

                            <section className="pc-sec">
                                <h2 className="pc-sec-tit">Cupom</h2>
                                <p className="pc-sec-sub">
                                    O incentivo que aparece para quem termina o cadastro.
                                </p>

                                <button type="button"
                                    className={`pc-switch ${cupomAtivo ? 'is-on' : ''}`}
                                    role="switch" aria-checked={cupomAtivo}
                                    onClick={() => setCupomAtivo(!cupomAtivo)}>
                                    <span className="pc-switch-track"><i /></span>
                                    <span>Mostrar cupom na página</span>
                                </button>

                                {cupomAtivo && (
                                    <div className="pc-cupom">
                                        <div className="pc-campo pc-w-cupom">
                                            <label className="pc-lab" htmlFor="pc-cupom">Código</label>
                                            <input id="pc-cupom" type="text" className="pc-inp"
                                                value={cupomCodigo} placeholder="Ex.: CUPOM5"
                                                onChange={e => setCupomCodigo(e.target.value)} />
                                        </div>
                                        <div className="pc-campo pc-w-texto">
                                            <label className="pc-lab" htmlFor="pc-cupom-txt">Instruções</label>
                                            <input id="pc-cupom-txt" type="text" className="pc-inp"
                                                value={cupomTexto}
                                                placeholder="Ex.: Apresente no caixa para 5% de desconto."
                                                onChange={e => setCupomTexto(e.target.value)} />
                                        </div>
                                    </div>
                                )}
                            </section>

                            <section className="pc-sec">
                                <h2 className="pc-sec-tit">Mensagem de agradecimento</h2>
                                <p className="pc-sec-sub">
                                    O que aparece depois do cadastro{cupomAtivo ? ', quando o cupom está desligado' : ''}.
                                </p>

                                <div className="pc-campo">
                                    <input type="text" className="pc-inp" maxLength={200}
                                        aria-label="Mensagem de agradecimento"
                                        value={agradecimento}
                                        placeholder="Ex.: Obrigado! Já pode usar seu benefício hoje mesmo."
                                        onChange={e => setAgradecimento(e.target.value)} />
                                </div>
                            </section>
                        </div>

                        {/* A divisória tem punho desenhado: o que redimensiona
                            é um controle visível, não uma borda que por acaso
                            arrasta. Setas também movem, de 20 em 20. */}
                        <div
                            className="pc-punho"
                            aria-label="Largura da prévia"
                            title="Arraste para alargar a prévia"
                            {...previa.propsDoPunho}
                        >
                            <span className="pc-punho-marca" aria-hidden="true" />
                        </div>

                        {/* ─── Prévia docada ──────────────────── */}
                        <aside className="pc-lado">
                            <div className="pc-lado-bloco">
                                <h2 className="pc-lado-tit">Prévia</h2>
                                <div className="pc-prev">
                                    <LandingPagePreview
                                        primaryColor={corPrimariaAdiada}
                                        bgColor={corFundoAdiada}
                                        textColor={corTextoAdiada}
                                        logoPath={logoPath}
                                        segmentsList={opcoes}
                                        promoActive={cupomAtivo}
                                        promoCode={cupomCodigo}
                                        promoText={cupomTexto}
                                        branchName={nomeEmpresa}
                                        headline={chamada}
                                        subheadline={apoio}
                                        thanksMessage={agradecimento}
                                        askSegment={pedeOrigem}
                                        askEmail={pedeEmail}
                                        askCity={pedeCidade}
                                    />
                                </div>
                            </div>

                            <div className="pc-lado-bloco">
                                <h2 className="pc-lado-tit">QR Code e link</h2>
                                <div className="pc-qr" ref={refQr}>
                                    {token
                                        ? <QRCodeSVG value={urlPublica} size={132} level="H" includeMargin={false} />
                                        : <p className="pc-hint">Sem link gerado ainda.</p>}
                                </div>

                                <p className="pc-url">{token ? `/p/${token}` : '—'}</p>

                                <div className="pc-lado-acts">
                                    <button type="button" className="pc-btn" onClick={copiarLink} disabled={!token}>
                                        <LuCopy size={13} /> Copiar link
                                    </button>
                                    <button type="button" className="pc-btn" onClick={baixarQr} disabled={!token}>
                                        <LuDownload size={13} /> Baixar QR
                                    </button>
                                </div>

                                <button type="button" className="pc-link-btn" onClick={gerarNovoToken}>
                                    <LuRefreshCw size={12} /> Gerar novo link
                                </button>
                                <p className="pc-hint">
                                    O link e o QR atuais param de funcionar.
                                </p>
                            </div>
                        </aside>
                    </div>
                )}
            </div>
        </div>
    );
};

export default PaginaDeCapturaPage;
